import './EditVehicle.scss';
import { useEffect, useState, useMemo, useRef, type JSX } from 'react';
import { TesterVehicleValidationSchema, TesterVehicle, VehicleDocumentsViewModel, StateLookUp, Document } from '../../../../interfaces/tester.interface';
import { reactPlugin } from '../../../../services/appInsights.service';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { Form, Row, Col, Button, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { Formik, FormikProps } from 'formik';
import { TesterService } from '../../../../services/tester.service';
import VehicleDocuemnts from '../VehicleDocuemnts/VehicleDocuemnts';
import { ComponentModelPopup } from '../../../../interfaces/modal.interface';
import ComponentModel from '../../appUtilityComponent/ComponentModel/ComponentModel';
import ErrorComponentModel from '../../appUtilityComponent/ErrorComponentModel/ErrorComponentModel';
import Spinner from '../../../components/Spinner';

type TParams = Readonly<{ vehicle: TesterVehicle, onCancel: any, onSave: any }>;

// Vehicle Type Selection Component
const VehicleTypeSelector = ({ formikProps }: { formikProps: FormikProps<TesterVehicle> }) => {
    const { values, handleChange, handleBlur } = formikProps;
    return (
        <Form.Group className={"mb-1 " + ((values.vehicleTypeId && values.vehicleTypeId.length > 0) ? "filled" : "")} as={Col} sm={12} lg={4}>
            <ToggleButtonGroup
                className="vehicle-type"
                type="radio"
                value={values.vehicleTypeId}
                name="vehicleTypeId"
                onBlur={handleBlur}>
                <ToggleButton id={`vehicle-type-automated-${values.vehicleId || 'new'}`} className="shadow-none vehicle-type-button" onChange={handleChange} variant="light" value={"1"}>Automated</ToggleButton>
                <ToggleButton id={`vehicle-type-pdd-${values.vehicleId || 'new'}`} className="shadow-none vehicle-type-button" onChange={handleChange} variant="light" value={"2"}>PDD</ToggleButton>
            </ToggleButtonGroup>
        </Form.Group>
    );
};

// VIN/Unique ID Input Component
const VehicleIdentifierInput = ({ formikProps }: { formikProps: FormikProps<TesterVehicle> }) => {
    const { values, handleChange, handleBlur, errors, touched } = formikProps;

    if (values.vehicleTypeId === "1") {
        return (
            <Form.Group className={"mb-1 form-floating " + ((values.vin && values.vin.length > 0) ? "filled" : "") + " required"} as={Col} sm={12} lg={4}>
                <Form.Control
                    autoComplete="off"
                    type="text"
                    name="vin"
                    maxLength={17}
                    placeholder='VIN'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.vin}
                    className={touched.vin && errors.vin ? "has-error" : ""}
                />
                <Form.Label className="mb-0">VIN</Form.Label>
                {(touched.vin && errors.vin) && <div className="text-danger font-small">{errors.vin}</div>}
            </Form.Group>
        );
    }

    return (
        <Form.Group className={"mb-1 form-floating " + ((values.uniqueNumber && values.uniqueNumber.length > 0) ? "filled" : "") + (values.vehicleTypeId === "2" ? " required " : "")} as={Col} sm={12} lg={4}>
            <Form.Control
                disabled={values.vehicleTypeId === ""}
                autoComplete="off"
                type="text"
                name="uniqueNumber"

                maxLength={17}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.uniqueNumber}
                className={touched.uniqueNumber && errors.uniqueNumber ? "has-error" : ""}
            />
            <Form.Label className="mb-0" for="uniqueNumber">{values.vehicleTypeId === "2" ? "Unique" : ""} ID</Form.Label>
            {(touched.uniqueNumber && errors.uniqueNumber) && <div className="text-danger font-small">{errors.uniqueNumber}</div>}
        </Form.Group>
    );
};

// Vehicle Details Component
const VehicleDetailsInputs = ({ formikProps, states, years }: { formikProps: FormikProps<TesterVehicle>, states: StateLookUp[], years: JSX.Element[] }) => {
    const { values, handleChange, handleBlur, errors, touched } = formikProps;

    return (
        <>
            <Form.Group className={"mb-1 form-floating " + ((values.vehicleRegistrationStateCd && values.vehicleRegistrationStateCd.length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={2}>
                <Form.Control
                    disabled={!(values.vehicleTypeId && values.vehicleTypeId.length > 0)}
                    autoComplete="off"
                    as="select"
                    name="vehicleRegistrationStateCd"
                    placeholder="State Issued"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.vehicleRegistrationStateCd}
                    className={touched.vehicleRegistrationStateCd && errors.vehicleRegistrationStateCd ? "has-error" : ""}
                >
                    <option value=""></option>
                    {states.map((state: StateLookUp) => (
                        <option key={state.StateCode} value={state.StateCode}>{state.StateCode}</option>
                    ))}
                </Form.Control>
                <span style={{ position: 'absolute', right: '10px', pointerEvents: 'none', top: '50%', transform: 'translateY(-50%)' }} className='vehicle-filter-icon'></span>
                <Form.Label className="mb-0">State Issued</Form.Label>
                {(touched.vehicleRegistrationStateCd && errors.vehicleRegistrationStateCd) && <div className="text-danger font-small">{errors.vehicleRegistrationStateCd}</div>}
            </Form.Group>

            <Form.Group className={"mb-1 form-floating " + ((values.licensePlateNumber && values.licensePlateNumber.length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={2}>
                <Form.Control
                    disabled={values.vehicleTypeId !== "1"}
                    autoComplete="off"
                    type="text"
                    name="licensePlateNumber"
                    maxLength={25}
                    placeholder="License Plate"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.licensePlateNumber}
                    className={touched.licensePlateNumber && errors.licensePlateNumber ? "has-error" : ""}
                />

                <Form.Label className="mb-0" >License Plate</Form.Label>
                {(touched.licensePlateNumber && errors.licensePlateNumber) && <div className="text-danger font-small">{errors.licensePlateNumber}</div>}
            </Form.Group>
        </>
    );
};

// Vehicle Specifications Component
const VehicleSpecifications = ({ formikProps, years }: { formikProps: FormikProps<TesterVehicle>, years: JSX.Element[] }) => {
    const { values, handleChange, handleBlur, errors, touched } = formikProps;

    return (
        <Col sm={12} lg={8}>
            <Row>
                <Form.Group className={"mb-1 form-floating " + ((values.year && values.year.toString().length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={4}>
                    <Form.Control
                        disabled={values.vehicleTypeId === ""}
                        autoComplete="off"
                        as="select"
                        placeholder='Year'
                        name="year"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.year}
                        className={touched.year && errors.year ? "has-error" : ""}
                    >
                        {years}
                    </Form.Control>
                    <span style={{ position: 'absolute', right: '10px', pointerEvents: 'none', top: '50%', transform: 'translateY(-50%)' }} className='vehicle-filter-icon'></span>
                    <Form.Label className="mb-0">Year</Form.Label>
                    {(touched.year && errors.year) && <div className="text-danger font-small">{errors.year}</div>}
                </Form.Group>

                <Form.Group className={"mb-1 form-floating " + ((values.vehicleMake && values.vehicleMake.toString().length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={4}>
                    <Form.Control
                        disabled={values.vehicleTypeId === ""}
                        autoComplete="off"
                        type="text"
                        name="vehicleMake"
                        placeholder="Make"
                        maxLength={100}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.vehicleMake}
                        className={touched.vehicleMake && errors.vehicleMake ? "has-error" : ""}
                    />
                    <Form.Label className="mb-0">Make</Form.Label>
                    {(touched.vehicleMake && errors.vehicleMake) && <div className="text-danger font-small">{errors.vehicleMake}</div>}
                </Form.Group>

                <Form.Group className={"mb-1 form-floating " + ((values.vehicleModel && values.vehicleModel.length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={4}>
                    <Form.Control
                        disabled={values.vehicleTypeId === ""}
                        autoComplete="off"
                        type="text"
                        name="vehicleModel"
                        placeholder="Model"
                        maxLength={100}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.vehicleModel}
                        className={touched.vehicleModel && errors.vehicleModel ? "has-error" : ""}
                    />
                    <Form.Label className="mb-0">Model</Form.Label>
                    {(touched.vehicleModel && errors.vehicleModel) && <div className="text-danger font-small">{errors.vehicleModel}</div>}
                </Form.Group>
            </Row>
        </Col>
    );
};

// Engine Type Component
const EngineTypeInput = ({ formikProps }: { formikProps: FormikProps<TesterVehicle> }) => {
    const { values, handleChange, handleBlur, errors, touched } = formikProps;

    return (
        <Col sm={12} lg={4}>
            <Row>
                <Form.Group className={"mb-1 form-floating " + ((values.vehicleEngineType && values.vehicleEngineType.length > 0) ? "filled" : "") + (values.vehicleTypeId.length > 0 ? " required " : "")} as={Col} sm={12} lg={values.vehicleEngineType === "Other" ? 6 : 12}>
                    <Form.Control
                        disabled={values.vehicleTypeId === ""}
                        autoComplete="off"
                        as="select"
                        name="vehicleEngineType"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.vehicleEngineType}
                        className={touched.vehicleEngineType && errors.vehicleEngineType ? "has-error" : ""}
                    >
                        <option value=""></option>
                        <option value="Gasoline">Gasoline</option>
                        <option value="Diesel">Diesel</option>
                        <option value="Electric">Electric</option>
                        <option value="Propane">Propane</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Natural Gas">Natural Gas</option>
                        <option value="Compressed Gas">Compressed Gas</option>
                        <option value="Flex Fuel">Flex Fuel</option>
                        <option value="Other">Other</option>
                    </Form.Control>
                    <span style={{ position: 'absolute', right: '10px', pointerEvents: 'none', top: '50%', transform: 'translateY(-50%)' }} className='vehicle-filter-icon'></span>
                    <Form.Label className="mb-0">Engine Type</Form.Label>
                    {(touched.vehicleEngineType && errors.vehicleEngineType) && <div className="text-danger font-small">{errors.vehicleEngineType}</div>}
                </Form.Group>
                {values.vehicleEngineType === "Other" &&
                    <Form.Group className={"mb-1 form-floating " + ((values.otherEngineType && values.otherEngineType.length > 0) ? "filled" : "") + " required"} as={Col} sm={12} lg={6}>
                        <Form.Control
                            disabled={values.vehicleTypeId === ""}
                            autoComplete="off"
                            type="text"
                            name="otherEngineType"
                            maxLength={50}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.otherEngineType}
                            className={touched.otherEngineType && errors.otherEngineType ? "has-error" : ""}
                        />
                        <Form.Label className="mb-0">Other Engine Type</Form.Label>
                        {(touched.otherEngineType && errors.otherEngineType) && <div className="text-danger font-small">{errors.otherEngineType}</div>}
                    </Form.Group>
                }
            </Row>
        </Col>
    );
};

// Documents Section Component
const DocumentsSection = ({ documentsUploaded, onVehicleDoumentsClick, vehicle }: { documentsUploaded: string[], onVehicleDoumentsClick: (vehicle: TesterVehicle) => void, vehicle: TesterVehicle }) => {
    return (
        <Col className="col-lg mw-md-300px border-right text-center">
            <Row>
                <Col className="mb-2 mt-2 documents-label text-center">CLICK BELOW TO UPLOAD REQUIRED FIELDS</Col>
            </Row>
            <Row>
                <Col>
                    {documentsUploaded.map(x =>
                        <Button key={x} variant="outline-secondary" className={"vehicle-doc-round-button btn-round me-2 btn-32-32 btn-doc-num " + (Number.isNaN(+x) ? "uploded-doc" : "")} onClick={() => onVehicleDoumentsClick(vehicle)}>{x}</Button>
                    )}
                </Col>
            </Row>
            {documentsUploaded.filter(x => Number.isNaN(+x)).length < 6 &&
                <Row>
                    <Col className="text-not-available-on-mobile-app">
                        NOT AVAILABLE ON MOBILE APP
                    </Col>
                </Row>
            }
        </Col>
    );
};

// Action Buttons Component
const ActionButtons = ({ values, isValid, onCancel, vehicleId }: { values: TesterVehicle, isValid: boolean, onCancel: (id: number) => void, vehicleId: number }) => {
    return (
        <Col className="col-lg mw-md-200px text-center">
            <Button className="btn-vehicle-round btn-save btn-default me-2" disabled={!(values.vehicleTypeId && values.vehicleTypeId.length > 0 && isValid)} variant="light" type="submit">SAVE</Button>
            <Button className="btn-vehicle-round btn-cancel btn-default" variant="light" onClick={() => onCancel(vehicleId)}>CANCEL</Button>
        </Col>
    );
};

function EditVehicle({ vehicle, onCancel, onSave }: TParams) {

    let [componentModal, setComponentModal] = useState({} as ComponentModelPopup);

    let [errorModal, setErrorModal] = useState({
        Title: "Error",
        Show: false,
        Component: null,
        HandleClose: () => setErrorModal((prev) => ({ ...prev, Show: false }))
    } as ComponentModelPopup);

    let [isShowLoading, setIsShowLoading] = useState(false as boolean);

    let [documentsUploaded, setDocumentsUploaded] = useState([] as string[]);

    let [vehicleDocs, setVehicleDocs] = useState({ RegistrationDocument: { documentId: 0 }, VehicleDocumentGroup: { documentGroupId: 0 } } as VehicleDocumentsViewModel);

    let [states, setStates] = useState([] as StateLookUp[]);

    const testerServiceRef = useRef(new TesterService());

    // Helper to display error messages
    const showError = (message: string) => {
        setErrorModal({
            Title: "Error",
            Show: true,
            Component: <div>{message}</div>,
            HandleClose: () => setErrorModal((prev) => ({ ...prev, Show: false }))
        });
    };

    useEffect(() => {
        GetStateLookUp();

        setDocumentsUploaded(vehicle.DocumentsUploaded);

    }, [vehicle]);

    const GetStateLookUp = () => {
        testerServiceRef.current.GetStateLookUp()
            .then(res => {
                if (res.statusCode === "N") {
                    const errorMessage = res.statusMessage || "Failed to load states";
                    console.error("GetStateLookUp error:", errorMessage);
                    showError(errorMessage);
                    return;
                }
                setStates(res.result || []);
            })
            .catch((error: any) => {
                const message = error?.statusMessage || error?.message || "An unexpected error occurred while loading states";
                console.error("GetStateLookUp exception:", error);
                showError(message);
            });
    }

    // Helper to attach documents to vehicle object
    const attachDocumentToVehicle = (vehicleObj: TesterVehicle) => {
        if (!vehicleDocs?.RegistrationDocument || (vehicleDocs.RegistrationDocument?.documentId ?? 0) <= 0) {
            return;
        }

        vehicleObj.documentId = vehicleDocs.RegistrationDocument?.documentId ?? 0;
        vehicleObj.documentGroupId = vehicleDocs.VehicleDocumentGroup?.documentGroupId ?? 0;
        vehicleObj.vehicleDocuments = [] as Document[];

        // Entry 1: sparse group-link record — matches backend join-table expectation
        // e.g. {documentGroupId: 87} as seen in SYST working payload
        if ((vehicleDocs.VehicleDocumentGroup?.documentGroupId ?? 0) > 0) {
            vehicleObj.vehicleDocuments.push({
                vehicleId: vehicleObj.vehicleId || null,
                documentGroupId: vehicleDocs.VehicleDocumentGroup?.documentGroupId ?? 0
            });
        }

        // Entry 2: sparse registration-document record — matches backend join-table expectation
        // e.g. {documentId: 511} as seen in SYST working payload
        vehicleObj.vehicleDocuments.push({
            vehicleId: vehicleObj.vehicleId || null,
            documentId: vehicleDocs.RegistrationDocument?.documentId ?? null,
            documentTitle: vehicleDocs.RegistrationDocument?.documentTitle ?? "",
            documentLocation: vehicleDocs.RegistrationDocument?.documentLocation ?? "",
            documentTypeId: vehicleDocs.RegistrationDocument?.documentTypeId ?? "REG",
            isActive: "Y"
        });
    };

    // Helper to save vehicle
    const saveVehicle = async (vehicleObj: TesterVehicle) => {
        try {
            const res = await testerServiceRef.current.SaveVehicle(vehicleObj);

            if (res.statusCode === "N") {
                const errorMessage = res.statusMessage || "Failed to save vehicle";
                console.error("SaveVehicle error:", errorMessage);
                showError(errorMessage);
                setIsShowLoading(false);
                return;
            }

            const vehicleType = vehicleObj.vehicleTypeId === "1" ? "Vehicle " + vehicleObj.vin : "PDD " + vehicleObj.uniqueNumber;
            const message = `${vehicleType} has been saved successfully.`;
            setIsShowLoading(false);
            onSave(message);
        } catch (error: any) {
            const message = error?.statusMessage || error?.message || "An unexpected error occurred while saving vehicle";
            console.error("SaveVehicle exception:", error);
            showError(message);
            setIsShowLoading(false);
        }
    };

    const OnFormSubmit = async (vehicleObj: TesterVehicle): Promise<void> => {
        setIsShowLoading(true);

        const isValid = await TesterVehicleValidationSchema.isValid(vehicleObj);
        if (!isValid) {
            setIsShowLoading(false);
            return;
        }

        attachDocumentToVehicle(vehicleObj);
        vehicleObj.isActive = "Y";
        await saveVehicle(vehicleObj);
    }

    const onClose = () => {
        setComponentModal({} as ComponentModelPopup);
    }

    const onDocumentsSave = (vehicleDocs: VehicleDocumentsViewModel) => {

        setVehicleDocs(vehicleDocs);

        const _docsUploaded = [] as string[];

        (vehicleDocs?.RegistrationDocument?.documentId ?? 0) ? _docsUploaded.push("R") : _docsUploaded.push("1");
        (vehicleDocs?.VehicleDocumentGroup?.documents?.map(x => x.documentTypeId)?.includes("SECURE")) ? _docsUploaded.push("S") : _docsUploaded.push("2");
        (vehicleDocs?.VehicleDocumentGroup?.documents?.map(x => x.documentTypeId)?.includes("EXTRICATION")) ? _docsUploaded.push("E") : _docsUploaded.push("3");
        (vehicleDocs?.VehicleDocumentGroup?.documents?.map(x => x.documentTypeId)?.includes("TOWING")) ? _docsUploaded.push("T") : _docsUploaded.push("4");
        (vehicleDocs?.VehicleDocumentGroup?.documents?.map(x => x.documentTypeId)?.includes("FIREFIGHTING")) ? _docsUploaded.push("F") : _docsUploaded.push("5");
        (vehicleDocs?.VehicleDocumentGroup?.documents?.map(x => x.documentTypeId)?.includes("POSTINCIDENT")) ? _docsUploaded.push("I") : _docsUploaded.push("6");

        setDocumentsUploaded(_docsUploaded);

        onClose();
    }

    const onVehicleDoumentsClick = (vehicle: TesterVehicle) => {
        setComponentModal({ Show: true, Title: "Vehicle Documents", HandleClose: () => { onClose() }, Component: <VehicleDocuemnts onClose={onClose} vehicle={vehicle} isReadonly={false} onDocumentsSave={onDocumentsSave} vehicleDocuments={vehicleDocs}></VehicleDocuemnts> });
    };

    const years = useMemo(() => {
        const yearsArray: JSX.Element[] = [];
        yearsArray.push(<option key={0} value=""></option>);
        const date = new Date();
        for (let year = 2011; year < date.getFullYear() + 2; year++) {
            yearsArray.push(<option key={year} value={year}>{year}</option>);
        }
        return yearsArray;
    }, []);
    return (
        <>
            <ErrorComponentModel modal={errorModal} />
            <Row className="animation-content">
                <Col className="col-lg">
                    {vehicle &&
                        <Formik validateOnBlur={true}
                            initialValues={vehicle}
                            validationSchema={TesterVehicleValidationSchema}
                            onSubmit={OnFormSubmit}
                        >
                            {(formikProps) => {
                                const { values, handleSubmit, isValid } = formikProps;

                                return (
                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            <Col className="border-right pr-1">
                                                <Row>
                                                    <VehicleTypeSelector formikProps={formikProps} />
                                                    <VehicleIdentifierInput formikProps={formikProps} />
                                                    <VehicleDetailsInputs formikProps={formikProps} states={states} years={years} />
                                                </Row>
                                                <Row>
                                                    <VehicleSpecifications formikProps={formikProps} years={years} />
                                                    <EngineTypeInput formikProps={formikProps} />
                                                </Row>
                                            </Col>
                                            <DocumentsSection documentsUploaded={documentsUploaded} onVehicleDoumentsClick={onVehicleDoumentsClick} vehicle={vehicle} />
                                            <ActionButtons values={values} isValid={isValid} onCancel={onCancel} vehicleId={vehicle.vehicleId} />
                                        </Row>
                                    </Form>
                                );
                            }}
                        </Formik>
                    }
                </Col>
                <ComponentModel modal={componentModal}></ComponentModel>
            </Row>
            {isShowLoading && <Spinner></Spinner>}
        </>
    );
}

export default withAITracking(reactPlugin, EditVehicle);